import React, { useState, useMemo, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Box, Drawer } from '@mui/material';
import { useAuth } from '../context/AuthContext';
import { useSnackbar } from './common/Snackbar';
import UserMenuPopup from './common/UserMenuPopup';
import ConfirmationDialog from './common/ConfirmationDialog';
import { useUserPermissions } from '../hooks/shared/useUserPermissions';
import { getMenuConfig } from '../config/sidebarMenu';
import { colors } from '../constants';
import {
  DRAWER_WIDTH,
  COLLAPSED_WIDTH,
  SIDEBAR_MENU_ID,
  SIDEBAR_MENU_BUTTON_ID,
  AAI_PLATFORM_URL,
} from '../constants/sidebar';
import SidebarHeader from './sidebar/SidebarHeader';
import SidebarMenuSection from './sidebar/SidebarMenuSection';
import SidebarExternalLink from './sidebar/SidebarExternalLink';
import SidebarUserProfile from './sidebar/SidebarUserProfile';

/**
 * Sidebar Component
 * Main navigation sidebar with role-based menu items
 * 
 * @param {boolean} isCollapsed - Whether the sidebar is collapsed
 * @param {function} onToggle - Callback function to toggle sidebar
 */
const Sidebar = ({ isCollapsed, onToggle }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, logout } = useAuth();
  const { showSnackbar } = useSnackbar();
  const permissions = useUserPermissions();
  
  const [anchorEl, setAnchorEl] = useState(null);
  const [logoutDialogOpen, setLogoutDialogOpen] = useState(false);

  // Get menu configuration based on permissions
  const menuSections = useMemo(() => getMenuConfig(permissions), [permissions]);

  // Check if a route is active
  const isActiveRoute = useCallback(
    (path) => {
    if (path === '/dashboard') {
      return location.pathname === '/dashboard';
    }
    return location.pathname.startsWith(path);
    },
    [location.pathname]
  );

  // Handle profile click to open user menu
  const handleProfileClick = (event) => {
    setAnchorEl(event.currentTarget);
  };

  // Handle menu close
  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  // Handle settings navigation
  const handleSettingsClick = () => {
    navigate('/dashboard/settings');
  };

  // Handle logout request
  const handleLogoutRequest = () => {
    setLogoutDialogOpen(true);
  };

  // Handle confirm logout
  const handleConfirmLogout = async () => {
    try {
      await logout();
      showSnackbar('Logged out successfully', 'success');
      // Redirect to external SSO portal
      window.location.href = AAI_PLATFORM_URL;
    } catch (error) {
      showSnackbar('Failed to logout. Please try again.', 'error');
    }
  };

  // Handle navigation
  const handleNavigate = useCallback(
    (path) => {
      navigate(path);
    },
    [navigate]
  );

  return (
    <Drawer
      variant="permanent"
      sx={{
        width: isCollapsed ? COLLAPSED_WIDTH : DRAWER_WIDTH,
        flexShrink: 0,
        transition: 'width 0.3s ease',
        '& .MuiDrawer-paper': {
          width: isCollapsed ? COLLAPSED_WIDTH : DRAWER_WIDTH,
          boxSizing: 'border-box',
          borderRight: 'none',
          background: colors.sidebarBackground,
          transition: 'width 0.3s ease',
          overflowX: 'hidden',
          boxShadow: `4px 0 28px -4px ${colors.sidebarShadow}, 2px 0 14px -2px ${colors.sidebarShadowAccent}`,
        },
      }}
    >
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          height: '100%',
          background: 'transparent',
        }}
      > 
        {/* Logo and Toggle Button */}
        <SidebarHeader isCollapsed={isCollapsed} onToggle={onToggle} />

        {/* Menu Sections - Scrollable */}
        <SidebarMenuSection
          menuSections={menuSections}
          isCollapsed={isCollapsed}
          isActiveRoute={isActiveRoute}
          onNavigate={handleNavigate}
        />

        {/* AAI Platform Link - Above User Profile */}
        <SidebarExternalLink
          isCollapsed={isCollapsed}
          url={AAI_PLATFORM_URL}
          label="AAI Platform"
        />

        {/* User Profile Section - Fixed at bottom */}
        {/* <SidebarUserProfile
          user={user}
          isCollapsed={isCollapsed}
          onProfileClick={handleProfileClick}
          menuButtonId={SIDEBAR_MENU_BUTTON_ID}
          menuId={SIDEBAR_MENU_ID}
          anchorEl={anchorEl}
        /> */}
      </Box>

      {/* User Menu Popup - Outside Drawer to appear outside sidebar */}
      <UserMenuPopup
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
        user={user}
        onSettingsClick={handleSettingsClick}
        onLogoutClick={handleLogoutRequest}
        menuId={SIDEBAR_MENU_ID}
        menuButtonId={SIDEBAR_MENU_BUTTON_ID}
      />
      
      {/* Logout Confirmation Dialog */}
      <ConfirmationDialog
        open={logoutDialogOpen}
        onClose={() => setLogoutDialogOpen(false)}
        onConfirm={handleConfirmLogout}
        title="Confirm Logout"
        message="Are you sure you want to logout? You will need to login again to access your account."
        confirmText="Logout"
        cancelText="Cancel"
        variant="danger"
      />
    </Drawer>
  );
};

export default Sidebar;
